import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSession } from "../../app/session.js";
import { trpc } from "../../app/trpc.js";
import { Badge, Button, Card, MenuRow, Notice, PageTitle, SectionTitle, Spinner, useWide } from "../../ui/index.js";
import type { BackState } from "./BackLink.js";
import { useCabinet } from "./context.js";
import { useLogout } from "./useLogout.js";

/**
 * «Ещё» (на широком экране «Настройки»): свои настройки для всех; настройки салона, отчёты, оплата и демо
 * только владельцу. На широком экране разделы в три колонки.
 * «Настройка салона» (мастер подключения) видна, пока он не завершён (решение 04.11.2026): потом всё,
 * что настраивалось по шагам, живёт на отдельных страницах. Пройти мастер заново можно из кабинета салона в админке
 * (там пункт остаётся) или по прямой ссылке.
 * Адрес, правила, FAQ и своё название типа после мастера правят на странице «Данные салона и правила».
 * Переходы отсюда помечают в истории, что пришли из меню: «Назад» в шапке тогда возвращается шагом истории,
 * и меню открывается на том же месте прокрутки, как после системной «назад» (`BackLink`).
 * Выход на телефоне здесь, на широком экране в боковой панели.
 */
export function MorePage() {
  const { t } = useTranslation();
  const wide = useWide();
  const navigate = useNavigate();
  const session = useSession();
  const cab = useCabinet();
  const onboarding = useQuery(trpc.onboarding.status.queryOptions({ salonId: cab.salonId }, { enabled: cab.isOwner }));
  const unknown = useQuery(trpc.bot.unknownQuestions.list.queryOptions({ salonId: cab.salonId, status: "open", limit: 200 }, { enabled: cab.isOwner }));
  const { logout, pending: leaving, error: logoutError } = useLogout();
  const go = (path: string) => () => navigate(`${cab.base}/${path}`, { state: { backTo: "settings" } satisfies BackState });

  // Админ в кабинете салона (пакет «подключим за вас») может пройти мастер и после завершения.
  const inAdmin = cab.base.startsWith("/admin/");
  if (cab.loading) return <Spinner />;
  const unknownCount = unknown.data?.length ?? 0;
  return (
    <div>
      <PageTitle>{wide ? t("nav.settings") : t("more.title")}</PageTitle>

      <div className="lg:grid lg:grid-cols-3 lg:items-start lg:gap-6">
        <div>
          <SectionTitle>{t("more.me")}</SectionTitle>
          <Card className="py-1">
            <MenuRow title={t("more.profile")} hint={session.user?.email} onClick={go("settings/profile")} />
            <MenuRow title={t("more.notifications")} onClick={go("settings/notifications")} />
            {cab.masterId && <MenuRow title={t("more.mySchedule")} hint={t("more.myScheduleHint")} onClick={go(`settings/masters/${cab.masterId}`)} />}
          </Card>
        </div>

        {cab.isOwner && (
          <>
            <div>
              <SectionTitle>{t("more.salon")}</SectionTitle>
              <Card className="py-1">
                {/* Пока статус грузится или не загрузился, пункт показываем: незавершённый салон не должен его терять. */}
                {(!onboarding.data?.completed || inAdmin) && (
                  <MenuRow
                    title={t("more.onboarding")}
                    hint={t("more.onboardingHint")}
                    right={onboarding.data && !onboarding.data.completed ? <Badge tone="amber">{t("more.onboardingTodo")}</Badge> : undefined}
                    onClick={go("onboarding")}
                  />
                )}
                <MenuRow title={t("more.salonProfile")} hint={t("more.salonProfileHint")} onClick={go("settings/salon")} />
                <MenuRow title={t("more.bookingLink")} hint={t("more.bookingLinkHint")} onClick={go("settings/booking-link")} />
                <MenuRow title={t("more.whoAnswers")} hint={t("more.whoAnswersHint")} onClick={go("settings/bot")} />
                <MenuRow
                  title={t("more.unknown")}
                  hint={t("more.unknownHint")}
                  right={unknownCount > 0 ? <Badge tone="amber">{unknownCount}</Badge> : undefined}
                  onClick={go("settings/unknown")}
                />
                <MenuRow title={t("more.services")} onClick={go("settings/services")} />
                <MenuRow title={t("more.masters")} hint={t("more.mastersHint")} onClick={go("settings/masters")} />
                <MenuRow title={t("more.calendarRules")} onClick={go("settings/calendar")} />
                <MenuRow title={t("more.team")} hint={t("more.teamHint")} onClick={go("settings/team")} />
                <MenuRow title={t("more.instagram")} hint={t("more.instagramHint")} onClick={go("settings/instagram")} />
              </Card>
            </div>

            <div>
              <SectionTitle>{t("more.business")}</SectionTitle>
              <Card className="py-1">
                <MenuRow title={t("more.reports")} onClick={go("reports")} />
                <MenuRow title={t("more.billing")} onClick={go("billing")} />
                <MenuRow title={t("more.demo")} hint={t("more.demoHint")} onClick={go("demo")} />
              </Card>
            </div>
          </>
        )}
      </div>

      {!wide && (
        <>
          {logoutError && (
            <div className="mt-8">
              <Notice kind="error">{t("common.error")}</Notice>
            </div>
          )}
          <Button variant="secondary" className={`${logoutError ? "mt-3" : "mt-8"} w-full`} onClick={() => void logout()} disabled={leaving}>
            {t("nav.logout")}
          </Button>
        </>
      )}
    </div>
  );
}
